import _ from 'lodash'
import * as THREE from 'three'

import { DXFWriter } from '@/utils/dxfUtils'
import MathsUtils from '@/utils/mathsUtils'
import WaterjetUtils from '@/apps/glass-production-station/src/classes/waterjetUtils'
import GlassSegment from '@/model/glassSegment'

import { AlignmentTypes, SegmentTypes } from '@/store/mathTypes'
import { OrderEditorCutoutTypes, OrderEditorGeometryShapeTypes, ProductionWaterjetReliefTypes, SheetAllocationStatusTypes, ProductionStationHardwareTypes } from '@/store/types'


/**
 * Class representing a ProductionPanel object
 * Used for display / calculation functions within production stations
 */
export default class ProductionPanel {
  constructor(panelData = {}, stationHardwareConfig = {}) {
    this.id = panelData.id
    this.barcode = panelData.barcode
    this.panelWidth = panelData.panelWidth
    this.panelHeight = panelData.panelHeight
    this.orderItem = _.cloneDeep(panelData.orderItem || {})
    this.optimiserSheetAllocations = _.cloneDeep(panelData.optimiserSheetAllocations || [])

    this.panelHasShapeData = this.orderItem.shapeData && this.orderItem.shapeData.cutouts && this.orderItem.shapeData.edgePoints

    this.calculatedCutoutPaths = []     // Calculated paths based on station info & panel transform
    this.stationHardwareConfig = _.cloneDeep(stationHardwareConfig || {})   // The ProductionStationHardwareConfig record applicable to this panel in the station

    // Waterjet relief cuts
    this.waterjetReliefCuts = []

    // Waterjet Cutout settings
    this.waterjetCutoutSettings = {}

    this.flipped = false
    this.rotationEdgeIndex = 0
    this.waterjetBayPosition = 0
    this.waterjetOriginOffsetX = 0
    this.waterjetOriginOffsetY = 0

    this.pixelAdjust = 0.5     // Sub-pixel adjustment to stop antialiasing creating blurry lines
    this.sheetColour = '#363A40'
    this.sheetOutlineColour = '#A0A0A0'
    this.panelColour = '#2F5075'
    this.panelBoundsColour = '#707070'
    this.dimBoundsLineColour = '#505050'
    this.dimBoundsTextColour = '#A0A0A0'
    this.outlineColour = '#B0B0B0'
    this.dimSquareColour = '#FFFFFF'
    this.dimAngleLabelColour = '#C7C7C7'
    this.dimLengthLabelColour = '#C7C7C7'
    this.cutoutColour = '#FF0000'
    this.cutoutDisabledColour = '#D0D0D0'
    this.cutoutAdditionalColour = '#FF4040'
    this.cutoutLeadInColour = '#008000'
    this.cutoutLeadOutColour = '#808000'
    this.cutoutEntryColour = '#00FF00'
    this.cutoutExitColour = '#FFFF00'
    this.textColour = '#A0A7B0'
    this.cutOrderBackgroundColour = '#17375D'
    this.cutOrderTextColour = '#D0FFFF'
    this.cutOrderCancelledBanStrokeStyle = '#FF0000'
  }


  /**
   * Calculate the straight edges on the panel
   * Straight edges / cuts will be done from cutting before waterjet
   * Used as rotation options
   */
  calculateStraightEdgeList() {
    let shapeData = this.orderItem.shapeData || {}

    if(_.isArray(shapeData.edgePoints)) {
      var edgeList = []

      shapeData.edgePoints.forEach((point, index) => {
        let start = point
        let end = shapeData.edgePoints[(index+1) % shapeData.edgePoints.length]

        let dirX = end.x - start.x
        let dirY = end.y - start.y
        let edgeAngle = MathsUtils.wrapAngleDegrees(MathsUtils.radiansToDegrees(Math.atan2(dirY, dirX)))

        // Outwards angle. Assumes edge points are clockwise
        let outAngle = MathsUtils.wrapAngleDegrees(MathsUtils.radiansToDegrees(Math.atan2(dirY, dirX)) - 90)

        edgeList.push({
          start: point,
          end: end,
          dirAngle: edgeAngle,
          outAngle: outAngle
        })
      })

      return edgeList
    }

    return []
  }


  /**
   * Calculate the default straight edge to use as the bottom of the panel
   * Attempts to rotate the panel so the longest edge is on the bottom
   *
   * Returns index of the straight edge of the panel
   */
  calculateDefaultRotationEdge(waterjetBedConfig) {
    let edgeList = this.calculateStraightEdgeList()

    var largestEdge = 0
    var largestIndex = -1

    edgeList.forEach((edge, index) => {
      let start = new THREE.Vector2(edge.start.x, edge.start.y)
      let end = new THREE.Vector2(edge.end.x, edge.end.y)

      let edgeLength = start.distanceTo(end)
      if(edgeLength > largestEdge || largestIndex == -1) {
        largestIndex = index
        largestEdge = edgeLength
      }
    })

    if(largestIndex == -1)
      largestIndex = 0

    return largestIndex
  }


  /**
   * Reset the flip/rotation details of the panel on the waterjet bed
   */
  waterjetReset(waterjetBedConfig) {
    this.flipped = false
    this.rotationEdgeIndex = this.calculateDefaultRotationEdge(waterjetBedConfig)
    this.waterjetOriginOffsetX = 0
    this.waterjetOriginOffsetY = 0
    this.waterjetCutoutSettings = {}

    // Disable any cutouts flagged at optimisation
    if(_.isArray(this.optimiserSheetAllocations) && this.panelHasShapeData) {
      this.optimiserSheetAllocations
        .filter(sheetAllocation => sheetAllocation.status == SheetAllocationStatusTypes.completed)
        .forEach(sheetAllocation => {
          if(_.isArray(sheetAllocation.cutoutList)) {
            sheetAllocation.cutoutList.forEach(optiCutout => {
              
              // Disable cutout by default
              this.setWaterjetCutoutEnabled(optiCutout.index, false)
            })
          }
        })
    }
  }


  /**
   * Toggle flipped state
   */
  waterjetToggleFlipped() {
    this.flipped = !this.flipped
  }


  /**
   * Get flipped status
   */
  waterjetIsFlipped() {
    return this.flipped
  }


  /**
   * Rotate the panel around left or right
   */
  waterjetRotate(direction) {
    let edgeCount = this.calculateStraightEdgeList().length
    if(direction > 0) {
      this.rotationEdgeIndex++
      this.rotationEdgeIndex %= edgeCount
    } else if(direction < 0) {
      if(this.rotationEdgeIndex > 0)
        this.rotationEdgeIndex--
      else
        this.rotationEdgeIndex = edgeCount - 1
    }

    // Reset offset when rotating
    this.waterjetOriginOffsetX = 0
    this.waterjetOriginOffsetY = 0
  }


  /**
   * Reset the origin offset point of the panel to butt up against the origin guide
   * Note: Only handle left side of the guide (ie, guide height) as the panel is always aligned to the bottom
   */
  waterjetResetOriginPoint(waterjetBedConfig) {
    this.waterjetOriginOffsetX = 0
    this.waterjetOriginOffsetY = 0

    // Ignore if origin guide not setup
    if(waterjetBedConfig.originGuideHeight == 0)
      return

    let transformedData = this.generateWaterjetPanelTransformedData(waterjetBedConfig)
    if(!transformedData)
      return

    var straightSegs = transformedData.straightEdgeList.map(edge => {
      return new GlassSegment(SegmentTypes.line, {
        startPosition: edge.start,
        endPosition: edge.end
      })
    })

    var upNormal = new THREE.Vector2(0, 1)
    var leftGuideTopPos = new THREE.Vector2(0, waterjetBedConfig.originGuideHeight)

    var guideRegionPointList = []

    straightSegs.forEach(seg => {
      // If point in region, include it
      if(seg.startPosition.y <= waterjetBedConfig.originGuideHeight)
        guideRegionPointList.push(seg.startPosition)
      if(seg.endPosition.y <= waterjetBedConfig.originGuideHeight)
        guideRegionPointList.push(seg.endPosition)

      // Check intersections at guide height
      var intPoint = MathsUtils.calculate2DLineSegmentPlaneIntersection(seg.startPosition, seg.endPosition, upNormal, leftGuideTopPos)
      if(intPoint)
        guideRegionPointList.push(intPoint)
    })

    // Use leftmost point within he guide line horizontal region
    var leftMostX = guideRegionPointList.reduce((curValue, point) => Math.min(curValue, point.x), 9999999);
    leftMostX = Math.max(leftMostX, 0)
    leftMostX -= this.waterjetBayPosition

    // Limit by negative space
    leftMostX = Math.min(leftMostX, waterjetBedConfig.maxNegativeWidth)

    this.waterjetOriginOffsetX = -MathsUtils.roundNumber(leftMostX, 1)
  }


  /**
   * Serialize the panel to waterjet station data
   */
  serializeWaterjetData() {
    return {
      flipped: this.flipped,
      rotationEdgeIndex: this.rotationEdgeIndex,
      originOffsetX: this.waterjetOriginOffsetX,
      originOffsetY: this.waterjetOriginOffsetY,
      waterjetCutoutSettings: this.waterjetCutoutSettings
    }
  }


  /**
   * Deserialize the panel waterjet config from station data
   */
  deserializeWaterjetData(waterjetData) {
    this.flipped = !!waterjetData.flipped
    this.rotationEdgeIndex = parseInt(waterjetData.rotationEdgeIndex || 0)

    this.waterjetOriginOffsetX = parseInt(waterjetData.originOffsetX || 0)
    this.waterjetOriginOffsetY = parseInt(waterjetData.originOffsetY || 0)

    let edgeCount = this.calculateStraightEdgeList().length
    this.rotationEdgeIndex = Math.max(this.rotationEdgeIndex, 0)
    this.rotationEdgeIndex = Math.min(this.rotationEdgeIndex, edgeCount - 1)
    this.waterjetCutoutSettings = waterjetData.waterjetCutoutSettings || {}
  }



  /**
   * Recalculate the CNC cutting paths for the panel at waterjet
   * Calculates the transformed cutting paths based on panel transform (rotation/flipped/bed position)
   * Calculates path order for optimised cutting flow
   */
  recalculateCNCPaths(waterjetBedConfig) {
    let transformedData = this.generateWaterjetPanelTransformedData(waterjetBedConfig)
    if(!transformedData)
      return

    var homePosition = new THREE.Vector2(waterjetBedConfig.homePositionX || 0, waterjetBedConfig.homePositionY || 0)

    this.calculatedCutoutPaths = transformedData.cutoutPathList.map((cutout, cutoutIndex) => {
      var leadInPath = []
      var leadOutPath = []
      var additionalCutPaths = []

      var entryPoint = new THREE.Vector2()
      var exitPoint = new THREE.Vector2()

      var useLeadInOutPath = true
      var isCenterCutout = cutout.type == OrderEditorCutoutTypes.centerCutOut
      var isCutoutAllArcs = cutout.path.every(segment => segment.type == SegmentTypes.arc)

      // Note: generateWaterjetPanelTransformedData() will ensure cutouts are the correct orientation for the Jetstream nozzle
      // Eg, left side of nozzle path is discarded

      // No lead in/out for empty paths
      if(cutout.path.length == 0)
        useLeadInOutPath = false

      if(cutout.path.length > 0) {
        entryPoint.copy(cutout.path[0].startPosition)
        exitPoint.copy(cutout.path[cutout.path.length - 1].endPosition)
      }

      // Center Cutout circles get handled differently & do not use lead in/out
      // Jetstream hardware handles lead in/out for circles itself
      if(cutout.type == OrderEditorCutoutTypes.centerCutOut) {
        if(cutout.geometryShapeType == OrderEditorGeometryShapeTypes.circle) {
          useLeadInOutPath = false

          // Entry/Exit points in the middle for display
          // DXF will handle this specially
          entryPoint = cutout.bounds.getCenter(entryPoint)
          exitPoint.copy(entryPoint)
        }
      }

      
      // Calculate the lead in / out paths
      if(useLeadInOutPath && _.isObject(this.stationHardwareConfig)) {
        var leadRadius = parseInt(this.stationHardwareConfig.waterjetLeadRadius || 0)
        var minRadiusLeadArc = parseInt(this.stationHardwareConfig.waterjetMinRadiusStartEndArc || 0)
        let edgeMaxDistance = 4     // On Jetstream devices, we cannot go out by more than 4mm into negative space (eg, -4,-4 are the limits)


        // Remove start/end arcs of the cutout if they're below the minimum radius lead size
        // Only for edge/corner cutouts that are not full arcs
        if(!isCenterCutout && !isCutoutAllArcs && minRadiusLeadArc > 0) {
          this._removeWaterjetCutoutLeadArcs(cutout.path, minRadiusLeadArc)
        }


        if(leadRadius > 0) {
          var startSegment = cutout.path[0]
          var endSegment = cutout.path[cutout.path.length - 1]
          if(isCenterCutout) {
            // Non-Circle Center cutouts

            if(cutout.geometryShapeType != OrderEditorGeometryShapeTypes.circle) {
              // For center cutouts, find the shortest line segment & split it in half & use as the points to connect to lead in/out paths
              var shortestSegmentIndex = -1
              var shortestSegLength = 0
              var midSegmentLength = leadRadius * 2

              cutout.path.forEach((segment, segIndex) => {
                if(segment.type == SegmentTypes.line) {
                  let segLength = segment.startPosition.distanceTo(segment.endPosition)

                  // Cannot lead into segments with a starting corner radius > 40% of the segment length
                  // Occurs with pill shaped cutouts. The lead in/out arcs intersect with the pill cutouts
                  if(segment.startCornerRadius >= segLength * 0.4)
                    return

                  if((segLength < shortestSegLength || shortestSegmentIndex == -1) && segLength >= midSegmentLength) {
                    shortestSegmentIndex = segIndex
                    shortestSegLength = segLength
                  }
                }
              })

              // Fallback: If cannot find valid spot for starting, just select the 1st segment
              if(shortestSegmentIndex == -1 && cutout.path.length > 0)
                shortestSegmentIndex = 0

              if(shortestSegmentIndex != -1) {
                // Rotate path elements around so this is the starting segment
                for(var i=0; i<shortestSegmentIndex; i++) {
                  cutout.path.push(cutout.path.shift())
                }

                // Lead into the middle of the segment
                // Cut the segment short & add rest of the segment at the end
                // Extend the lead out segment so the lead in/out points don't all meet up together & confuse waterjet software
                var leadIntoSegment = cutout.path[0]
                var splicePosition = new THREE.Vector2((leadIntoSegment.startPosition.x + leadIntoSegment.endPosition.x) / 2.0, (leadIntoSegment.startPosition.y + leadIntoSegment.endPosition.y) / 2.0)
                let segmentDir = leadIntoSegment.getDirection()

                var leadOutSegment = new GlassSegment(leadIntoSegment.type, {
                  startCornerRadius: leadIntoSegment.startCornerRadius,
                  endCornerRadius: 0,
                  startPosition: leadIntoSegment.startPosition.clone(),
                  endPosition: splicePosition.clone().addScaledVector(segmentDir, Math.max(leadRadius, 1.0))    // Overshoot splice position
                })
                leadIntoSegment.startCornerRadius = 0
                leadIntoSegment.startPosition.copy(splicePosition)

                cutout.path.push(leadOutSegment)
                
                // Add in the lead in J arc
                let segDir = leadIntoSegment.endPosition.clone().sub(leadIntoSegment.startPosition)
                segDir.normalize()

                let leadDir = new THREE.Vector2(segDir.y, -segDir.x)
                if(cutout.windingReversed)
                  leadDir.negate()

                leadInPath = this.generateWaterjetLeadIn(leadIntoSegment.startPosition, segDir, leadDir, leadRadius, cutout.windingReversed)

                // Add in the lead out with an arc
                leadOutPath = this.generateWaterjetLeadOut(leadOutSegment.endPosition, segDir, leadDir, leadRadius, cutout.windingReversed)
              }
            }

          } else {
            // Corner/Edge cutouts
            // Note: Adding extra segments to a lead in/out path creates conflicts in the DXF path detection on the Jetstream hardware
            // Multiple segments from the path & shape outline in the DXF can connect at the same point & Jetstream cannot detect the path correctly
            // So we'll just modify the cutout path & extend out instead of adding lead in/out paths seperately

            if(startSegment.type == SegmentTypes.line) {
              var dir = startSegment.endPosition.clone().sub(startSegment.startPosition)
              dir.normalize()

              if(startSegment.startCornerRadius <= 0) {
                // Extend line back out
                startSegment.startPosition.addScaledVector(dir, -leadRadius)
                this.clipWaterjetSegmentToExtents(startSegment, waterjetBedConfig, edgeMaxDistance)
              }
              
            }
            else if(startSegment.type == SegmentTypes.arc) {
              // Just cut in with a straight line angled slightly out from the arc start angle
              // Based on feedback from Robbie
              // BUT no more than 4mm away from the edge, as that is the limit for waterjet when against the origin axis

              let startAngle = MathsUtils.wrapAngleDegrees(startSegment.arcStartAngle + (startSegment.arcClockwise ? -90 : 90))
              let entryAngle = MathsUtils.wrapAngleDegrees(startAngle + (startSegment.arcClockwise ? 25 : -25))
              let leadAngle = MathsUtils.wrapAngleDegrees(startAngle)

              let leadDir = MathsUtils.generate2DDirectionVector(MathsUtils.degreesToRadians(leadAngle))
              let leadRightDir = MathsUtils.generate2DDirectionVector(MathsUtils.degreesToRadians(entryAngle))

              let leadASegment = new GlassSegment(SegmentTypes.line, {
                startCornerRadius: 0,
                endCornerRadius: 0
              })
              leadASegment.startPosition.copy(startSegment.startPosition.clone().addScaledVector(leadDir, leadRadius).addScaledVector(leadRightDir, leadRadius))
              leadASegment.endPosition.copy(startSegment.startPosition.clone())

              this.clipWaterjetSegmentToExtents(leadASegment, waterjetBedConfig, edgeMaxDistance)

              leadInPath.push(leadASegment)
            }


            // Lead Out
            if(endSegment.type == SegmentTypes.line) {
              let dir = endSegment.endPosition.clone().sub(endSegment.startPosition)
              dir.normalize()

              if(endSegment.endCornerRadius <= 0) {
                // Extend back out
                endSegment.endPosition.addScaledVector(dir, leadRadius)
                
                this.clipWaterjetSegmentToExtents(endSegment, waterjetBedConfig, edgeMaxDistance)
              }              
              
            }
            else if(endSegment.type == SegmentTypes.arc) {

              // Just cut in with a straight line angled slightly out from the arc start angle
              // Based on feedback from Robbie
              let endAngle = MathsUtils.wrapAngleDegrees(endSegment.arcEndAngle + (endSegment.arcClockwise ? 90 : -90))
              let exitAngle = MathsUtils.wrapAngleDegrees(endAngle + (endSegment.arcClockwise ? -25 : 25))
              let leadAngle = MathsUtils.wrapAngleDegrees(endAngle)

              let leadDir = MathsUtils.generate2DDirectionVector(MathsUtils.degreesToRadians(leadAngle))
              let leadRightDir = MathsUtils.generate2DDirectionVector(MathsUtils.degreesToRadians(exitAngle))

              let leadASegment = new GlassSegment(SegmentTypes.line, {
                startCornerRadius: 0,
                endCornerRadius: 0
              })
              leadASegment.startPosition.copy(endSegment.endPosition.clone())
              leadASegment.endPosition.copy(endSegment.endPosition.clone().addScaledVector(leadDir, leadRadius).addScaledVector(leadRightDir, leadRadius))

              this.clipWaterjetSegmentToExtents(leadASegment, waterjetBedConfig, edgeMaxDistance)

              leadOutPath.push(leadASegment)
            }
          }
        }
      }

      // Update entry/exit points
      // Ignore for circle center cutouts
      if(cutout.type != OrderEditorCutoutTypes.centerCutOut || cutout.geometryShapeType != OrderEditorGeometryShapeTypes.circle) {
        if(cutout.path.length > 0) {
          entryPoint.copy(cutout.path[0].startPosition)
          exitPoint.copy(cutout.path[cutout.path.length - 1].endPosition)
        }
        if(leadInPath.length > 0)
          entryPoint.copy(leadInPath[0].startPosition)
        if(leadOutPath.length > 0)
          exitPoint.copy(leadOutPath[leadOutPath.length - 1].endPosition)
      }

      // Relief cuts
      if(cutout.type == OrderEditorCutoutTypes.cornerCutOut || cutout.type == OrderEditorCutoutTypes.edgeCutOut) {
        var reliefLeadRadius = 0
        var reliefMinRadius = 3     // Hardcoded if no lead radius set
        if(_.isObject(this.stationHardwareConfig))
          reliefLeadRadius = parseInt(this.stationHardwareConfig.waterjetLeadRadius || 0)

        reliefLeadRadius = Math.max(reliefLeadRadius, reliefMinRadius)

        let cutoutReliefCuts = this.waterjetReliefCuts.filter(relief => relief.cutoutIndex == cutoutIndex)
        if(cutoutReliefCuts.length > 0) {

          let inlineReliefCut = cutoutReliefCuts.find(relief => relief.type == ProductionWaterjetReliefTypes.inline)
          if(inlineReliefCut) {
            try {
              this._insertWaterjetInlineReliefCut(cutout.path, inlineReliefCut.distanceFromEdge, inlineReliefCut.reliefCutHeight, reliefLeadRadius, transformedData.edgePoints)
              inlineReliefCut.valid = true
            } catch(err) {
              inlineReliefCut.valid = false
              inlineReliefCut.errorMessage = err
            }
          }

          let largeReliefCut = cutoutReliefCuts.find(relief => relief.type == ProductionWaterjetReliefTypes.large)
          if(largeReliefCut) {
            try {
              this._insertWaterjetLargeReliefCut(cutout.path, cutout.bounds, largeReliefCut.distanceInwards, reliefLeadRadius, reliefLeadRadius, transformedData.edgePoints, transformedData.shapePath, additionalCutPaths)
              largeReliefCut.valid = true
            } catch(err) {
              largeReliefCut.valid = false
              largeReliefCut.errorMessage = err
            }
          }
        }
      }

      // Set cutout settings
      var cutoutSettings = this.waterjetCutoutSettings[cutoutIndex]
      var cutoutEnabled = true
      if(cutoutSettings && cutoutSettings.enabled !== undefined)
        cutoutEnabled = cutoutSettings.enabled

      return {
        index: cutoutIndex,
        type: cutout.type,
        enabled: cutoutEnabled,
        geometryShapeType: cutout.geometryShapeType,
        cutOrder: cutoutIndex,
        radius: cutout.radius || 0,
        entryPoint: entryPoint,
        exitPoint: exitPoint,
        leadInPath: leadInPath,
        leadOutPath: leadOutPath,
        path: cutout.path,
        additionalCutPaths: additionalCutPaths,
        bounds: cutout.bounds,
        windingReversed: cutout.windingReversed,
        sourceCutoutBounds: cutout.sourceCutoutBounds,
        displayPointList: cutout.displayPointList
      }
    })


    // Calculate the best cutting order
    var cuttingOrder = WaterjetUtils.generateCuttingPathOrder(this.calculatedCutoutPaths, homePosition, true)
    cuttingOrder.forEach((cutoutIndex, cutOrderIndex) => {
      this.calculatedCutoutPaths[cutoutIndex].cutOrder = cutOrderIndex
    })
  }


  /**
   * Remove the lead in/out arcs for a cutout path on the waterjet if they are below the minimum radius
   */
  _removeWaterjetCutoutLeadArcs(segmentList, minLeadArcRadius) {
    if(segmentList.length <= 2)
      return
    
    var startSeg = segmentList[0]
    var endSeg = segmentList[segmentList.length - 1]
    
    if(startSeg.type == SegmentTypes.arc) {
      // Next segment must be a line to extend
      if(startSeg.arcRadius < minLeadArcRadius && segmentList[1].type == SegmentTypes.line) {

        // Extend next line
        var nextLine = segmentList[1]
        nextLine.startPosition = MathsUtils.project2DPointOnPlane(nextLine.startPosition, nextLine.getDirection(), startSeg.startPosition)

        segmentList.splice(0, 1)
      }
    }

    if(endSeg.type == SegmentTypes.arc) {
      // Previous segment must be a line to extend
      if(endSeg.arcRadius < minLeadArcRadius && segmentList[segmentList.length - 2].type == SegmentTypes.line) {

        // Extend prev line
        var prevLine = segmentList[segmentList.length - 2]
        prevLine.endPosition = MathsUtils.project2DPointOnPlane(prevLine.endPosition, prevLine.getDirection(), endSeg.endPosition)

        segmentList.splice(segmentList.length - 1, 1)
      }
    }
  }


  /**
   * Clip a cutting segment on the waterjet to the maximum hardware extents
   * If the segment goes over the limit, clips the segment
   */
  clipWaterjetSegmentToExtents(segment, waterjetBedConfig, maxExtents) {
    var maxWidth = waterjetBedConfig.width + maxExtents
    var maxHeight = waterjetBedConfig.height + maxExtents
    var minX = -maxExtents
    var minY = -maxExtents

    var scaleX = waterjetBedConfig.originHorizontal == AlignmentTypes.right ? -1.0 : 1.0
    var scaleY = waterjetBedConfig.originVertical == AlignmentTypes.bottom ? -1.0 : 1.0

    // Only handles lines currently
    if(segment.type == SegmentTypes.line) {
      // Setup 4 planes
      // Normal is pointing out
      var extentPlanes = [
        { normal: new THREE.Vector2(0, scaleY), point: new THREE.Vector2(0, minY) },
        { normal: new THREE.Vector2(scaleX, 0), point: new THREE.Vector2(maxWidth, 0) },
        { normal: new THREE.Vector2(0, -scaleY), point: new THREE.Vector2(0, maxHeight) },
        { normal: new THREE.Vector2(-scaleX, 0), point: new THREE.Vector2(minX, 0) },
      ]

      extentPlanes.forEach(planeInfo => {
        var intersect = MathsUtils.calculate2DLineSegmentPlaneIntersection(segment.startPosition, segment.endPosition, planeInfo.normal, planeInfo.point)
        if(intersect) {

          if(MathsUtils.calculateDistanceToPlane2D(planeInfo.normal, planeInfo.point, segment.startPosition) < 0)
            segment.endPosition.copy(intersect)
          else
            segment.startPosition.copy(intersect)
        }
      })
    }
  }


  /**
   * Generate the J shaped waterjet lead in
   * Used for non-circle center cutouts
   */
  generateWaterjetLeadIn(entryPosition, entryDir, rightDir, leadRadius, arcClockwise) {
    var arcStartPos = entryPosition.clone().addScaledVector(entryDir, -leadRadius)
    var arcCenterPosition = entryPosition.clone().addScaledVector(rightDir, leadRadius)
    arcStartPos.addScaledVector(rightDir, leadRadius)

    let leadASegment = new GlassSegment(SegmentTypes.line, {
      startCornerRadius: 0,
      endCornerRadius: 0,
      startPosition: arcStartPos.clone().addScaledVector(rightDir, leadRadius),
      endPosition: arcStartPos.clone()
    })
    var leadBSegment = new GlassSegment(SegmentTypes.arc, {
      arcCenterPosition: arcCenterPosition,
      arcRadius: leadRadius,
      arcClockwise: arcClockwise,
      arcStartAngle: 0,
      arcEndAngle: 0
    })

    leadBSegment.startPosition.copy(arcStartPos)
    leadBSegment.endPosition.copy(entryPosition)
    leadBSegment.arcStartAngle = MathsUtils.calculateArcPointAngle(leadBSegment.arcCenterPosition, leadBSegment.startPosition)
    leadBSegment.arcEndAngle = MathsUtils.calculateArcPointAngle(leadBSegment.arcCenterPosition, leadBSegment.endPosition)
    leadBSegment.arcUpdated()

    return [leadASegment, leadBSegment]
  }


  /**
   * Generate the arc shaped waterjet lead out
   * Used for non-circle center cutouts
   */
  generateWaterjetLeadOut(exitPosition, exitDir, rightDir, leadRadius, arcClockwise) {
    var arcCenterPosition = exitPosition.clone().addScaledVector(rightDir, leadRadius)
    var arcEndPos = arcCenterPosition.clone().addScaledVector(exitDir, leadRadius)

    var leadASegment = new GlassSegment(SegmentTypes.arc, {
      arcCenterPosition: arcCenterPosition,
      arcRadius: leadRadius,
      arcClockwise: arcClockwise,
      arcStartAngle: 0,
      arcEndAngle: 0
    })

    leadASegment.startPosition.copy(exitPosition)
    leadASegment.endPosition.copy(arcEndPos)
    leadASegment.arcStartAngle = MathsUtils.calculateArcPointAngle(leadASegment.arcCenterPosition, leadASegment.startPosition)
    leadASegment.arcEndAngle = MathsUtils.calculateArcPointAngle(leadASegment.arcCenterPosition, leadASegment.endPosition)
    leadASegment.arcUpdated()

    return [leadASegment]
  }


  /**
   * Insert the waterjet inline relief cut into a cutting path for a corner/edge cutout
   * Cut is inserted into the transformed path
   * Throws error if unable to insert the relief cut
   */
  _insertWaterjetInlineReliefCut(path, distanceFromEdge, reliefCutHeight, reliefRadius, edgePoints) {
    let edgeSpaceDist = 2.5
    var intersectionPoint = new THREE.Vector2()

    // Find a straight segment from the end of the cutout that has a point within min/max relief distance to the nearest edge
    // Note: If corner radiuses used, keep away from ends by corner radius
    for(var i=path.length-1; i>=0; i--) {
      if(path[i].type == SegmentTypes.line) {
        let dir = path[i].getDirection()
        let leftDir = path[i].getOutDirection().negate()

        let castStartPoint = path[i].endPosition.clone().addScaledVector(dir, -(edgeSpaceDist + reliefRadius))   // Back up a bit from edge
        let castEndPoint = path[i].endPosition.clone().addScaledVector(dir, distanceFromEdge * 2)

        // Cast out & check intersection point with the edge
        // Go back from intersection by 'distanceFromEdge' and see if that point is on this segment
        if(MathsUtils.checkSegment2DPolygonIntersection(castStartPoint, castEndPoint, edgePoints, intersectionPoint)) {
          let reliefStartPoint = intersectionPoint.clone().addScaledVector(dir, -distanceFromEdge)

          let dist = MathsUtils.calculateDistanceToPlane2D(dir, path[i].startPosition, reliefStartPoint)
          if(dist >= path[i].startCornerRadius && dist <= path[i].getLength() - path[i].endCornerRadius) {

            // Calculate relief dir based on relief height
            var reliefDir = new THREE.Vector2()
            reliefDir.copy(intersectionPoint)
            reliefDir.addScaledVector(leftDir, reliefCutHeight)
            reliefDir.sub(reliefStartPoint)
            reliefDir.normalize()

            // Calculate intersection of relief path with edge
            var reliefEndPoint = new THREE.Vector2()
            let castReliefEndPoint = reliefStartPoint.clone().addScaledVector(reliefDir, distanceFromEdge * 4)
            if(MathsUtils.checkSegment2DPolygonIntersection(reliefStartPoint, castReliefEndPoint, edgePoints, reliefEndPoint)) {

              // Extend past the edge by the radius to ensure it cuts off
              reliefEndPoint.addScaledVector(reliefDir, reliefRadius*2)

              // Suitable to start from here
              // Insert inline relief at 45 degrees left
              let startIndex = i
              var reliefPath = []

              let returnStartPos = reliefEndPoint.clone().addScaledVector(new THREE.Vector2(-reliefDir.y, reliefDir.x), reliefRadius * 2)
              let returnEndPos = returnStartPos.clone().addScaledVector(reliefDir, -(reliefEndPoint.distanceTo(reliefStartPoint) + reliefRadius*2))

              // Relief path
              reliefPath.push(new GlassSegment(SegmentTypes.line, {
                startPosition: reliefStartPoint,
                endPosition: reliefEndPoint,
                startCornerRadius: reliefRadius,
                endCornerRadius: reliefRadius
              }))
              reliefPath.push(new GlassSegment(SegmentTypes.line, {
                startPosition: reliefEndPoint,
                endPosition: returnStartPos,
                startCornerRadius: reliefRadius,
                endCornerRadius: reliefRadius
              }))
              reliefPath.push(new GlassSegment(SegmentTypes.line, {
                startPosition: returnStartPos,
                endPosition: returnEndPos,
                startCornerRadius: reliefRadius,
                endCornerRadius: reliefRadius
              }))
              reliefPath.push(new GlassSegment(SegmentTypes.line, {
                startPosition: returnEndPos,
                endPosition: reliefStartPoint,
                startCornerRadius: reliefRadius,
                endCornerRadius: reliefRadius
              }))

              // Reconnect back to path
              reliefPath.push(new GlassSegment(SegmentTypes.line, {
                startPosition: reliefStartPoint,
                endPosition: path[startIndex].endPosition.clone(),
                startCornerRadius: reliefRadius,
                endCornerRadius: path[startIndex].endCornerRadius
              }))

              path[startIndex].endPosition.copy(reliefPath[0].startPosition)
              path[startIndex].endCornerRadius = reliefPath[0].startCornerRadius

              reliefPath.forEach((newSeg, index) => {
                path.splice(startIndex+index+1, 0, newSeg)
              })
              
              return true
            }
          }
        }        
      }
    }

    throw 'Unable to find a valid point to insert relief cut'
  }


  /**
   * Insert the waterjet large relief cut into a cutting path for a corner/edge cutout
   * Cut is added as an extra transformed path
   * Throws error if unable to insert the relief cut
   */
  _insertWaterjetLargeReliefCut(path, cutoutBounds, distanceInwards, leadRadius, reliefRadius, edgePoints, shapePath, additionalCutPaths) {
    if(path.length == 0)
      throw 'Invalid cutout path'

    // Large Relief Cut
    // Gets an axis aligned bounding box of the path & scale it inwards by distance
    // Generate 4 lines for each edge of the bounding box
    // Check intersections with shape/edge points of each line cast outwards
    // Generate relief path from the valid lines

    let pathStart = path[0].startPosition
    let pathEnd = path[path.length-1].endPosition

    let pathMinSize = Math.min(cutoutBounds.max.x - cutoutBounds.min.x, cutoutBounds.max.y - cutoutBounds.min.y)
    let pathMaxSize = Math.max(cutoutBounds.max.x - cutoutBounds.min.x, cutoutBounds.max.y - cutoutBounds.min.y)

    if(distanceInwards >= pathMinSize/2)
      throw 'Distance to too large'

    let pathDir = pathEnd.clone().sub(pathStart)
    pathDir.normalize()

    var adjustedBox = cutoutBounds.clone().expandByVector(new THREE.Vector2(-distanceInwards, -distanceInwards))
    let scaledBox = new THREE.Box2()
    scaledBox.expandByPoint(adjustedBox.min)
    scaledBox.expandByPoint(adjustedBox.max)

    // Axis align the relief cut for simplicity
    var cutPoints = [
      new THREE.Vector2(scaledBox.min.x, scaledBox.min.y),
      new THREE.Vector2(scaledBox.max.x, scaledBox.min.y),
      new THREE.Vector2(scaledBox.max.x, scaledBox.max.y),
      new THREE.Vector2(scaledBox.min.x, scaledBox.max.y),
    ]

    var reliefCutPath = []
    
    cutPoints.forEach((cutPoint, index) => {
      let nextPoint = cutPoints[(index+1) % cutPoints.length]

      var lineStartPoint = cutPoint.clone()
      var lineEndPoint = nextPoint.clone()

      // Cast out points in outwards direction
      // If don't hit the shape at all, ignore this line
      let outDir = MathsUtils.calculateLineOutDirection(lineStartPoint, lineEndPoint)
      let dir = MathsUtils.calculateLineDirection(lineStartPoint, lineEndPoint)

      let castEnd1 = lineStartPoint.clone().addScaledVector(outDir, pathMaxSize * 2)
      let castEnd2 = lineEndPoint.clone().addScaledVector(outDir, pathMaxSize * 2)

      let castEnd1Intersect = shapePath.find(shapeSegment => shapeSegment.calculateLineIntersection(lineStartPoint, castEnd1).length > 0)
      let castEnd2Intersect = shapePath.find(shapeSegment => shapeSegment.calculateLineIntersection(lineEndPoint, castEnd2).length > 0)

      // If neither point hits the shape, ignore it
      if(!castEnd1Intersect && !castEnd2Intersect)
        return

      // Cast the line outwards until it hits the edge points of the shape
      // But cannot hit the shape
      let castInline1 = lineStartPoint.clone().addScaledVector(dir, -pathMaxSize * 2)
      let castInline2 = lineEndPoint.clone().addScaledVector(dir, pathMaxSize * 2)
      let castInline1Intersect = shapePath.find(shapeSegment => shapeSegment.calculateLineIntersection(lineStartPoint, castInline1).length > 0)
      let castInline2Intersect = shapePath.find(shapeSegment => shapeSegment.calculateLineIntersection(lineEndPoint, castInline2).length > 0)

      // Cannot both not hit the shape
      // If both hit the shape, we don't alter it at all
      if(!castInline1Intersect && !castInline2Intersect)
        return

      if(!castInline1Intersect) {
        // Cast to hit the edge points
        var intStart = new THREE.Vector2()
        if(MathsUtils.checkSegment2DPolygonIntersection(lineStartPoint, castInline1, edgePoints, intStart)) {
          lineStartPoint.copy(intStart)
          // Add lead in
          lineStartPoint.addScaledVector(dir, -leadRadius)
        }
      }
      if(!castInline2Intersect) {
        // Cast to hit the edge points
        var intEnd = new THREE.Vector2()
        if(MathsUtils.checkSegment2DPolygonIntersection(lineEndPoint, castInline2, edgePoints, intEnd)) {
          lineEndPoint.copy(intEnd)

          // Add lead out
          lineEndPoint.addScaledVector(dir, leadRadius)
        }
      }

      reliefCutPath.push(
        new GlassSegment(SegmentTypes.line, {
          startPosition: lineStartPoint,
          endPosition: lineEndPoint
        })
      )
    })

    // Connect up the lines so they flow into a valid path
    // If not possible, don't add the relief cut
    var resultPath = MathsUtils.connectSegmentListToPath(reliefCutPath)
    if(!resultPath) {
      // Attempt reverse
      // Just array flip, don't flip segments, otherwise that'll flip the positions & result in same problem
      resultPath = MathsUtils.connectSegmentListToPath(reliefCutPath.reverse())
      if(!resultPath)
        throw 'Invalid large relief cut path'
    }

    reliefCutPath = resultPath

    // Check if intersects the shape, then not valid
    let shapeInts = MathsUtils.checkGlassSegmentListsIntersections(reliefCutPath, shapePath)
    if(shapeInts.length > 0)
      throw 'Invalid large relief cut path'

    // Add corner radiuses between connections
    reliefCutPath.forEach((segment, index) => {
      if(index > 0)
        segment.startCornerRadius = reliefRadius
      if(index < reliefCutPath.length-1)
        segment.endCornerRadius = reliefRadius
    })

    additionalCutPaths.push(reliefCutPath)
  }
  

  /**
   * Draw the panel for waterjet display
   */
  drawWaterjetDisplay(context, waterjetBedConfig, displayOptions, rulerSize, originOffsetX, originOffsetY, scaleX, scaleY, canvasWidth, canvasHeight) {
    let shapeData = this.orderItem.shapeData || {}
    let cutoutLineWidth = 2
    let cutoutMarkerLineWidth = 1

    // Render rectangle if no shape data available
    if(!this.panelHasShapeData) {
      this.drawNoShapeDataRectangle(context, waterjetBedConfig, rulerSize, originOffsetX, originOffsetY, scaleX, scaleY, canvasWidth, canvasHeight)
      return
    }

    if(!_.isArray(shapeData.path))
      return

    // Convert all paths to the coordinate system (used for rendering, click & DXF output)
    let transformedData = this.generateWaterjetPanelTransformedData(waterjetBedConfig)

    let transformPointX = (coordinate) => {
      return Math.round(coordinate * scaleX) + this.pixelAdjust
    }
    let transformPointY = (coordinate) => {
      return Math.round(coordinate * scaleY) + this.pixelAdjust
    }

    context.save()
    context.lineWidth = 1
    context.lineJoin = 'round'
    context.lineCap = 'round'

    var viewScaleX = 1
    var viewScaleY = 1
    var offsetX = 0
    var offsetY = 0

    switch(waterjetBedConfig.originHorizontal) {
      case AlignmentTypes.left:
        offsetX = rulerSize + originOffsetX
        break
      case AlignmentTypes.right:
        offsetX = canvasWidth - rulerSize - originOffsetX
        viewScaleX = -1
        break
    }
    switch(waterjetBedConfig.originVertical) {
      case AlignmentTypes.top:
        offsetY = rulerSize + originOffsetY
        break
      case AlignmentTypes.bottom:
        offsetY = canvasHeight - rulerSize - originOffsetY
        viewScaleY = -1
        break
    }

    context.setTransform(1, 0, 0, 1, 0, 0)      // Identity
    context.translate(offsetX, offsetY)
    context.scale(viewScaleX, viewScaleY)

    // Show total bounds
    this.renderPanelWJBounds(context, transformedData, scaleX, scaleY, viewScaleX, viewScaleY)

    // Edge shape
    context.strokeStyle = this.sheetOutlineColour
    context.fillStyle = this.sheetColour
    context.beginPath()

    transformedData.edgePoints.forEach((point, index) => {
      let nextEdgePoint = transformedData.edgePoints[(index+1) % transformedData.edgePoints.length]

      if(index == 0)
        context.moveTo(transformPointX(point.x), transformPointY(point.y))
      context.lineTo(transformPointX(nextEdgePoint.x), transformPointY(nextEdgePoint.y))
    })

    context.stroke()
    context.fill()

    context.strokeStyle = this.outlineColour
    context.fillStyle = this.panelColour
    context.beginPath()

    // Actual shape display
    this.renderPath(context, transformedData.shapePath, scaleX, scaleY)

    context.fill()
    context.stroke()

    // Draw square angle markers on the shape path
    this.renderPathAngles(context, transformedData.shapePath, scaleX, scaleY, viewScaleX, viewScaleY)

    // Draw the shape dimensions
    this.renderPathDimensions(context, transformedData.shapePath, scaleX, scaleY, viewScaleX, viewScaleY)

    // Cutouts
    let cutoutEntryExitPointSize = 5 / scaleX
    this.calculatedCutoutPaths.forEach(cutout => {
      if(cutout.path.length == 0)
        return

      context.strokeStyle = cutout.enabled ? this.cutoutColour : this.cutoutDisabledColour
      context.lineWidth = cutoutLineWidth

      context.beginPath()
      this.renderPath(context, cutout.path, scaleX, scaleY)
      context.stroke()

      // Additional paths (eg, large relief cuts)
      cutout.additionalCutPaths.forEach(additionalCutPath => {
        context.strokeStyle = this.cutoutAdditionalColour
        context.lineWidth = cutoutLineWidth
        context.beginPath()
        this.renderPath(context, additionalCutPath, scaleX, scaleY)
        context.stroke()

        // Start/end positions
        var pathStartPosition = additionalCutPath[0].startPosition
        var pathEndPosition = additionalCutPath[additionalCutPath.length - 1].endPosition

        context.strokeStyle = this.cutoutEntryColour
        context.lineWidth = cutoutMarkerLineWidth
        context.beginPath()
        context.moveTo(transformPointX(pathStartPosition.x), transformPointY(pathStartPosition.y - cutoutEntryExitPointSize))
        context.lineTo(transformPointX(pathStartPosition.x), transformPointY(pathStartPosition.y + cutoutEntryExitPointSize))
        context.moveTo(transformPointX(pathStartPosition.x - cutoutEntryExitPointSize), transformPointY(pathStartPosition.y))
        context.lineTo(transformPointX(pathStartPosition.x + cutoutEntryExitPointSize), transformPointY(pathStartPosition.y))
        context.stroke()

        context.strokeStyle = this.cutoutExitColour
        context.lineWidth = cutoutMarkerLineWidth
        context.beginPath()
        context.moveTo(transformPointX(pathEndPosition.x), transformPointY(pathEndPosition.y - cutoutEntryExitPointSize))
        context.lineTo(transformPointX(pathEndPosition.x), transformPointY(pathEndPosition.y + cutoutEntryExitPointSize))
        context.moveTo(transformPointX(pathEndPosition.x - cutoutEntryExitPointSize), transformPointY(pathEndPosition.y))
        context.lineTo(transformPointX(pathEndPosition.x + cutoutEntryExitPointSize), transformPointY(pathEndPosition.y))
        context.stroke()
      })

      // Lead in / out paths
      if(cutout.leadInPath.length > 0) {
        context.strokeStyle = this.cutoutLeadInColour
        context.beginPath()
        this.renderPath(context, cutout.leadInPath, scaleX, scaleY)
        context.stroke()
      }

      if(cutout.leadOutPath.length > 0) {
        context.strokeStyle = this.cutoutLeadOutColour
        context.beginPath()
        this.renderPath(context, cutout.leadOutPath, scaleX, scaleY)
        context.stroke()
      }

      // Draw entry/exit points
      context.strokeStyle = this.cutoutEntryColour
      context.lineWidth = cutoutMarkerLineWidth
      context.beginPath()
      context.moveTo(transformPointX(cutout.entryPoint.x), transformPointY(cutout.entryPoint.y - cutoutEntryExitPointSize))
      context.lineTo(transformPointX(cutout.entryPoint.x), transformPointY(cutout.entryPoint.y + cutoutEntryExitPointSize))
      context.moveTo(transformPointX(cutout.entryPoint.x - cutoutEntryExitPointSize), transformPointY(cutout.entryPoint.y))
      context.lineTo(transformPointX(cutout.entryPoint.x + cutoutEntryExitPointSize), transformPointY(cutout.entryPoint.y))
      context.stroke()

      context.strokeStyle = this.cutoutExitColour
      context.lineWidth = cutoutMarkerLineWidth
      context.beginPath()
      context.moveTo(transformPointX(cutout.exitPoint.x), transformPointY(cutout.exitPoint.y - cutoutEntryExitPointSize))
      context.lineTo(transformPointX(cutout.exitPoint.x), transformPointY(cutout.exitPoint.y + cutoutEntryExitPointSize))
      context.moveTo(transformPointX(cutout.exitPoint.x - cutoutEntryExitPointSize), transformPointY(cutout.exitPoint.y))
      context.lineTo(transformPointX(cutout.exitPoint.x + cutoutEntryExitPointSize), transformPointY(cutout.exitPoint.y))
      context.stroke()

      // Display cutting order tag
      let cutoutBoundsCenter = cutout.bounds.getCenter(new THREE.Vector2())
      let cutoutBoundsSize = cutout.bounds.getSize(new THREE.Vector2())
      let orderDisplayRadius = 10
      let orderDisplaySpacing = 5
      let displayOffset = orderDisplayRadius / scaleX + orderDisplaySpacing
      let orderDisplayCenterY = cutoutBoundsCenter.y + cutoutBoundsSize.y / 2 + displayOffset
      
      if(displayOptions.showCutoutOrder) {
        context.fillStyle = this.cutOrderBackgroundColour
        context.beginPath()
        context.ellipse(transformPointX(cutoutBoundsCenter.x), transformPointY(orderDisplayCenterY), orderDisplayRadius, orderDisplayRadius, 0, 0, 2*Math.PI)
        context.fill()
      }

      // Show ban lines if not enabled
      if(!cutout.enabled) {
        let banLineSize = (orderDisplayRadius / scaleX) / Math.sqrt(2)
        context.save()
        context.strokeStyle = this.cutOrderCancelledBanStrokeStyle
        context.lineWidth = 2
        context.beginPath()
        context.moveTo(transformPointX(cutoutBoundsCenter.x - banLineSize), transformPointY(orderDisplayCenterY + banLineSize))
        context.lineTo(transformPointX(cutoutBoundsCenter.x + banLineSize), transformPointY(orderDisplayCenterY - banLineSize))
        context.moveTo(transformPointX(cutoutBoundsCenter.x - banLineSize), transformPointY(orderDisplayCenterY - banLineSize))
        context.lineTo(transformPointX(cutoutBoundsCenter.x + banLineSize), transformPointY(orderDisplayCenterY + banLineSize))
        context.stroke()
        context.restore()
      }

      // Cutting order number
      if(cutout.enabled && displayOptions.showCutoutOrder) {
        context.save()
        context.translate(transformPointX(cutoutBoundsCenter.x), transformPointY(cutoutBoundsCenter.y + cutoutBoundsSize.y / 2 + displayOffset - 1))
        context.scale(Math.sign(viewScaleX), Math.sign(viewScaleY))
        context.fillStyle = this.cutOrderTextColour
        context.textAlign = 'center'
        context.textBaseline = 'middle'
        context.font = '15px sans-serif'
        context.fillText(`${cutout.cutOrder+1}`, 0, 0)
        context.restore()
      }

    })

    context.restore()
  }


  /**
   * Render a path to the context
   */
  renderPath(context, path, scaleX, scaleY) {
    let transformPointX = (coordinate) => {
      return Math.round(coordinate * scaleX) + this.pixelAdjust
    }
    let transformPointY = (coordinate) => {
      return Math.round(coordinate * scaleY) + this.pixelAdjust
    }


    path.forEach((segment, segmentIndex) => {
      let prevSegment = path[segmentIndex == 0 ? path.length-1 : segmentIndex-1]
      let nextSegment = path[segmentIndex == path.length-1 ? 0 : segmentIndex+1]

      if(segmentIndex == 0) {
        if(segment.type == SegmentTypes.line) {
          if(segment.startCornerRadius == 0.0) {
            context.moveTo(transformPointX(segment.startPosition.x), transformPointY(segment.startPosition.y))
          } else {
            // Adjust for the corner radius
            let roundCornerData = MathsUtils.calculateCornerRounding(
              new THREE.Vector2(prevSegment.startPosition.x, prevSegment.startPosition.y),
              new THREE.Vector2(prevSegment.endPosition.x, prevSegment.endPosition.y),
              new THREE.Vector2(segment.startPosition.x, segment.startPosition.y),
              new THREE.Vector2(segment.endPosition.x, segment.endPosition.y),
              segment.startCornerRadius,
              false
            )

            if(!roundCornerData) {
              context.moveTo(transformPointX(segment.startPosition.x), transformPointY(segment.startPosition.y))
            } else {
              context.moveTo(transformPointX(roundCornerData.segment2Start.x), transformPointY(roundCornerData.segment2Start.y))
            }
          }
        }
      }

      if(segment.type == SegmentTypes.line) {
        if(segment.endCornerRadius == 0.0)
          context.lineTo(transformPointX(segment.endPosition.x), transformPointY(segment.endPosition.y))
        else {
          // Adjust for the corner radius
          let roundCornerData = MathsUtils.calculateCornerRounding(
            new THREE.Vector2(segment.startPosition.x, segment.startPosition.y),
            new THREE.Vector2(segment.endPosition.x, segment.endPosition.y),
            new THREE.Vector2(nextSegment.startPosition.x, nextSegment.startPosition.y),
            new THREE.Vector2(nextSegment.endPosition.x, nextSegment.endPosition.y),
            segment.endCornerRadius,
            false
          )

          if(!roundCornerData) {
            context.lineTo(transformPointX(segment.endPosition.x), transformPointY(segment.endPosition.y))
          } else {
            context.lineTo(transformPointX(roundCornerData.segment1End.x), transformPointY(roundCornerData.segment1End.y))
            context.ellipse(transformPointX(roundCornerData.center.x),
                            transformPointY(roundCornerData.center.y),
                            roundCornerData.radius * scaleX,
                            roundCornerData.radius * scaleY,
                            0,
                            roundCornerData.startAngle,
                            roundCornerData.endAngle,
                            roundCornerData.clockwise)
          }
        }

      } else if(segment.type == SegmentTypes.arc) {
        context.ellipse(transformPointX(segment.arcCenterPosition.x),
                        transformPointY(segment.arcCenterPosition.y),
                        segment.arcRadius * scaleX,
                        segment.arcRadius * scaleY,
                        0,
                        MathsUtils.degreesToRadians(segment.arcStartAngle),
                        MathsUtils.degreesToRadians(segment.arcEndAngle),
                        !segment.arcClockwise)
      }
    })
  }


  /**
   * Render the panel bounds for the waterjet display
   */
  renderPanelWJBounds(context, transformedData, scaleX, scaleY, viewScaleX, viewScaleY) {
    let transformPointX = (coordinate) => {
      return Math.round(coordinate * scaleX) + this.pixelAdjust
    }
    let transformPointY = (coordinate) => {
      return Math.round(coordinate * scaleY) + this.pixelAdjust
    }

    let displayExtents = 30 / scaleX
    let wjBounds = MathsUtils.calculateGlassSegmentListBounds(transformedData.shapePath)

    let lblBoundsTop = new THREE.Vector2((wjBounds.min.x + wjBounds.max.x) / 2, wjBounds.max.y + displayExtents)
    let lblBoundsRight = new THREE.Vector2(wjBounds.max.x + displayExtents, (wjBounds.min.y + wjBounds.max.y) / 2)

    // Draw lines
    context.strokeStyle = this.dimBoundsLineColour
    context.lineWidth = 1
    context.beginPath()
    context.moveTo(transformPointX(wjBounds.min.x), transformPointY(lblBoundsTop.y))
    context.lineTo(transformPointX(wjBounds.max.x), transformPointY(lblBoundsTop.y))
    context.moveTo(transformPointX(lblBoundsRight.x), transformPointY(wjBounds.max.y))
    context.lineTo(transformPointX(lblBoundsRight.x), transformPointY(wjBounds.min.y))
    context.stroke()


    context.save()
    context.translate(transformPointX(lblBoundsTop.x), transformPointY(lblBoundsTop.y))
    context.scale(Math.sign(viewScaleX), Math.sign(viewScaleY))
    context.fillStyle = this.dimBoundsTextColour
    context.textAlign = 'center'
    context.textBaseline = 'bottom'
    context.font = '11px sans-serif'
    context.fillText(Math.round(wjBounds.max.x - wjBounds.min.x) + 'mm', 0, 0)
    context.restore()

    context.save()
    context.translate(transformPointX(lblBoundsRight.x), transformPointY(lblBoundsRight.y))
    context.rotate(-Math.PI/2)
    context.scale(Math.sign(viewScaleX), Math.sign(viewScaleY))
    context.fillStyle = this.dimBoundsTextColour
    context.textAlign = 'center'
    context.textBaseline = 'bottom'
    context.font = '11px sans-serif'
    context.fillText(Math.round(wjBounds.max.y - wjBounds.min.y) + 'mm', 0, 0)
    context.restore()

  }


  /**
   * Render the angles in a path between each connecting lines for the waterjet
   * Displays square angles for 90degree corners, otherwise displays the angle value
   */
  renderPathAngles(context, path, scaleX, scaleY, viewScaleX, viewScaleY) {
    let transformPointX = (coordinate) => {
      return Math.round(coordinate * scaleX) + this.pixelAdjust
    }
    let transformPointY = (coordinate) => {
      return Math.round(coordinate * scaleY) + this.pixelAdjust
    }

    let minSegmentLength = 75
    let angleDisplayPrecision = 2   // JB: 14/07/22 - Set to 2, 1 decimal place was causing rounding issues
    let labelOffset = 12.5 / scaleX
    let squareSize = 10 / scaleX

    path.forEach((segment, segmentIndex) => {
      let nextSegment = path[segmentIndex == path.length-1 ? 0 : segmentIndex+1]

      // Only display angles between 2 straight lines
      if(segment.type == SegmentTypes.line && nextSegment.type == SegmentTypes.line) {
        let primaryDirection = segment.getDirection()
        let angleDirection = nextSegment.getDirection()

        let primaryDirDegrees = MathsUtils.wrapAngleDegrees(MathsUtils.radiansToDegrees(MathsUtils.get2DVectorAngle(primaryDirection)))
        let angleDirDegrees = MathsUtils.wrapAngleDegrees(MathsUtils.radiansToDegrees(MathsUtils.get2DVectorAngle(angleDirection)))

        let angle = MathsUtils.roundNumber(MathsUtils.calculateShortestAngleDistanceDegrees(primaryDirDegrees, angleDirDegrees), angleDisplayPrecision)

        // Don't show anything if 0 or 180 degrees
        if(MathsUtils.roundNumber(Math.abs(angle - 180.0), angleDisplayPrecision) == 0 || MathsUtils.roundNumber(Math.abs(angle), angleDisplayPrecision) == 0)
          return

        // Ignore if either segment is too small
        if(segment.getLength() < minSegmentLength || nextSegment.getLength() < minSegmentLength)
          return

        let isSquareAngle = MathsUtils.roundNumber(Math.abs(angle - 90), angleDisplayPrecision) == 0
        if(isSquareAngle) {
          // Square angle marker
          let sqDisplayPoint = segment.endPosition
          let sqStartPnt = new THREE.Vector3(sqDisplayPoint.x - primaryDirection.x * squareSize, sqDisplayPoint.y - primaryDirection.y * squareSize)
          let sqMidPoint = sqStartPnt.clone().addScaledVector(angleDirection, squareSize)
          let sqEndPoint = new THREE.Vector3(sqDisplayPoint.x + angleDirection.x * squareSize, sqDisplayPoint.y + angleDirection.y * squareSize)

          context.strokeStyle = this.dimSquareColour
          context.lineWidth = 1
          context.beginPath()
          context.moveTo(transformPointX(sqStartPnt.x), transformPointY(sqStartPnt.y))
          context.lineTo(transformPointX(sqMidPoint.x), transformPointY(sqMidPoint.y))
          context.lineTo(transformPointX(sqEndPoint.x), transformPointY(sqEndPoint.y))
          context.stroke()
        } else {
          // Display angle label
          let units = '°'
          let text = MathsUtils.roundNumber(angle, angleDisplayPrecision).toString() + units

          let lblDisplayPoint = segment.endPosition
          let lblStartPnt = new THREE.Vector3(lblDisplayPoint.x - primaryDirection.x * labelOffset, lblDisplayPoint.y - primaryDirection.y * labelOffset)
          let lblMidPoint = lblStartPnt.clone().addScaledVector(angleDirection, labelOffset)

          context.save()
          context.translate(transformPointX(lblMidPoint.x), transformPointY(lblMidPoint.y))
          context.scale(Math.sign(viewScaleX), Math.sign(viewScaleY))
          context.fillStyle = this.dimAngleLabelColour
          context.textAlign = 'center'
          context.textBaseline = 'middle'
          context.font = '9px sans-serif'
          context.fillText(text, 0, 0)
          context.restore()
        }
      }
    })
  }


  /**
   * Render the dimensions for all line segments on the main path
   * Hides dimensions for small segments
   */
  renderPathDimensions(context, path, scaleX, scaleY, viewScaleX, viewScaleY) {
    let transformPointX = (coordinate) => {
      return Math.round(coordinate * scaleX) + this.pixelAdjust
    }
    let transformPointY = (coordinate) => {
      return Math.round(coordinate * scaleY) + this.pixelAdjust
    }

    var minSegmentLength = 75
    var labelOffset = 12.0 / scaleX
    var maxReadableAngle = 110

    path.forEach(segment => {

      if(segment.type == SegmentTypes.line) {
        let outDirection = segment.getOutDirection().negate()
        let segLength = segment.getLength()
        let displayAngle = MathsUtils.wrapAngleDegrees(MathsUtils.radiansToDegrees(segment.getAngle()))

        // Ignore if either segment is too small
        if(segLength < minSegmentLength)
          return

        // Flip the label direction if upside down
        if(displayAngle >= 180)
          displayAngle -= 180

        let readableDist = MathsUtils.calculateShortestAngleDistanceDegrees(0, displayAngle)
        if(readableDist > maxReadableAngle)
          displayAngle = MathsUtils.wrapAngleDegrees(displayAngle - 180)

        // Display length label
        let units = 'mm'
        let text = MathsUtils.roundNumber(segLength, 0).toString() + units

        let lblDisplayPoint = segment.getMidpoint()
        let lblMidPoint = new THREE.Vector3(lblDisplayPoint.x - outDirection.x * labelOffset, lblDisplayPoint.y - outDirection.y * labelOffset)

        context.save()
        context.translate(transformPointX(lblMidPoint.x), transformPointY(lblMidPoint.y))
        context.rotate(MathsUtils.degreesToRadians(displayAngle))
        context.scale(Math.sign(viewScaleX), Math.sign(viewScaleY))
        context.fillStyle = this.dimLengthLabelColour
        context.textAlign = 'center'
        context.textBaseline = 'middle'
        context.font = '10px sans-serif'
        context.fillText(text, 0, 0)
        context.restore()
      }
    })
  }


  /**
   * Draw the panel as no shape data version
   * Draws a rectangle with 'No Shape Data Available' in the panel
   */
  drawNoShapeDataRectangle(context, waterjetBedConfig, rulerSize, originOffsetX, originOffsetY, scaleX, scaleY, canvasWidth, canvasHeight) {
    let transformPointX = (coordinate) => {
      return Math.round(coordinate * scaleX) + this.pixelAdjust
    }
    let transformPointY = (coordinate) => {
      return Math.round(coordinate * scaleY) + this.pixelAdjust
    }

    var offsetX = 0
    var offsetY = 0

    switch(waterjetBedConfig.originHorizontal) {
      case AlignmentTypes.left:
        offsetX = rulerSize + originOffsetX
        break
      case AlignmentTypes.right:
        offsetX = canvasWidth - (this.panelWidth * scaleX) - rulerSize - originOffsetX
        break
    }
    switch(waterjetBedConfig.originVertical) {
      case AlignmentTypes.top:
        offsetY = rulerSize + originOffsetY
        break
      case AlignmentTypes.bottom:
        offsetY = canvasHeight - (this.panelHeight * scaleY) - rulerSize - originOffsetY
        break
    }

    context.save()
    context.lineWidth = 1
    context.lineJoin = 'round'
    context.lineCap = 'round'
    context.translate(offsetX, offsetY)
    context.translate(this.waterjetBayPosition * scaleX, 0)

    context.strokeStyle = this.sheetOutlineColour
    context.fillStyle = this.sheetColour

    context.beginPath()
    context.moveTo(transformPointX(0), transformPointY(0))
    context.lineTo(transformPointX(this.panelWidth), transformPointY(0))
    context.lineTo(transformPointX(this.panelWidth), transformPointY(this.panelHeight))
    context.lineTo(transformPointX(0), transformPointY(this.panelHeight))
    context.lineTo(transformPointX(0), transformPointY(0))

    context.stroke()
    context.fill()

    context.fillStyle = this.textColour
    context.textAlign = 'center'
    context.textBaseline = 'middle'
    context.font = '15px sans-serif'
    context.fillText('No panel shape data found', transformPointX(this.panelWidth / 2), transformPointY(this.panelHeight / 2))

    context.restore()    
  }


  /**
   * Generate all the panel waterjet data with rotations/translation/scale
   * 
   * @return {Object} Returns an object containing all the data required for displaying/exporting/processing the panel with waterjet
   */
  generateWaterjetPanelTransformedData(waterjetBedConfig) {
    if(!this.panelHasShapeData)
      return null

    let shapeData = this.orderItem.shapeData

    var flipPathWinding = false
    if(waterjetBedConfig.originVertical == AlignmentTypes.bottom)
      flipPathWinding = !flipPathWinding
    if(waterjetBedConfig.originHorizontal == AlignmentTypes.right)
      flipPathWinding = !flipPathWinding


    // All output data is post transform
    var outputData = {
      angle: 0,
      scaleX: (this.flipped ? -1.0 : 1.0) * (waterjetBedConfig.originHorizontal == AlignmentTypes.right ? -1.0 : 1.0),
      scaleY: waterjetBedConfig.originVertical == AlignmentTypes.bottom ? -1.0 : 1.0,
      bounds: new THREE.Box2(),
      straightEdgeList: [],
      edgePoints: [],
      shapePath: [],
      cutoutPathList: []
    }

    let straightEdgeList = this.calculateStraightEdgeList()

    var rotationDegrees = 90-straightEdgeList[this.rotationEdgeIndex].outAngle
    if(waterjetBedConfig.originVertical == AlignmentTypes.top)
      rotationDegrees += 180

    rotationDegrees = MathsUtils.wrapAngleDegrees(rotationDegrees)
    var rotationRads = MathsUtils.degreesToRadians(rotationDegrees)

    // Bounds
    var edgeBounds = new THREE.Box2()
    straightEdgeList.forEach(straightEdge => {
      edgeBounds.expandByPoint(new THREE.Vector2(straightEdge.start.x, straightEdge.start.y))
      edgeBounds.expandByPoint(new THREE.Vector2(straightEdge.end.x, straightEdge.end.y))
    })
    let edgeCenterPoint = edgeBounds.getCenter(new THREE.Vector2())

    // Calculate bounds of the rotated panel
    let rotateMatrix = new THREE.Matrix3()
    rotateMatrix.translate(-edgeCenterPoint.x, -edgeCenterPoint.y)
    rotateMatrix.rotate(-rotationRads)
    rotateMatrix.scale(outputData.scaleX, outputData.scaleY)

    var rotatedEdgeBounds = new THREE.Box2()
    straightEdgeList.forEach(straightEdge => {
      var startEdge = new THREE.Vector2(straightEdge.start.x, straightEdge.start.y)
      var endEdge = new THREE.Vector2(straightEdge.end.x, straightEdge.end.y)

      startEdge.applyMatrix3(rotateMatrix)
      endEdge.applyMatrix3(rotateMatrix)

      rotatedEdgeBounds.expandByPoint(startEdge)
      rotatedEdgeBounds.expandByPoint(endEdge)
    })

    rotateMatrix.translate(-rotatedEdgeBounds.min.x + this.waterjetBayPosition + this.waterjetOriginOffsetX, -rotatedEdgeBounds.min.y + this.waterjetOriginOffsetY)

    outputData.straightEdgeList = straightEdgeList.map(straightEdge => {
      var startEdge = new THREE.Vector2(straightEdge.start.x, straightEdge.start.y)
      var endEdge = new THREE.Vector2(straightEdge.end.x, straightEdge.end.y)

      startEdge.applyMatrix3(rotateMatrix)
      endEdge.applyMatrix3(rotateMatrix)

      outputData.bounds.expandByPoint(startEdge)
      outputData.bounds.expandByPoint(endEdge)

      return {
        start: startEdge,
        end: endEdge
      }
    })

    if(_.isArray(shapeData.edgePoints)) {
      shapeData.edgePoints.forEach(point => {
        var edgePoint = new THREE.Vector2(point.x, point.y)
        edgePoint.applyMatrix3(rotateMatrix)

        outputData.edgePoints.push(edgePoint)
      })
    }

    // Shape Path
    if(_.isArray(shapeData.path)) {
      outputData.shapePath = this.transformPath(shapeData.path, rotateMatrix, outputData.scaleX, outputData.scaleY)
    }

    // Cutouts
    if(_.isArray(shapeData.cutouts)) {
      outputData.cutoutPathList = shapeData.cutouts.map((cutout, index) => {
        var outputCutout = {
          index: index,
          type: cutout.type,
          geometryShapeType: cutout.geometryShapeType,
          radius: parseFloat(cutout.radius || 0),
          path: this.transformPath(cutout.path, rotateMatrix, outputData.scaleX, outputData.scaleY),
          windingReversed: cutout.type == OrderEditorCutoutTypes.centerCutOut ? flipPathWinding : !flipPathWinding
        }

        let displayPoints = (cutout.displayPointList || []).map(displayPoint => new THREE.Vector2(displayPoint.x, displayPoint.y))

        // Center cutouts must be counter-clockwise so the left side of the nozzle is pointing into the discarded portion
        if(cutout.type == OrderEditorCutoutTypes.centerCutOut) {
          let isClockwise = MathsUtils.getGlassSegmentListOrientation(outputCutout.path) < 0
          if(flipPathWinding)
            isClockwise = !isClockwise
          if(isClockwise)
            MathsUtils.reverseGlassSegmentList(outputCutout.path)

        } else {
          // Shape cutouts must be counter-clockwise
          // Use the displayPointList field for shape cutouts to help calculate orientation
          // The displayPointList has extra points connected to the hull to help reduce intersections in the path against itself
          let transformedDisplayPointList = displayPoints.map(displayPoint => {
            let pntVec = new THREE.Vector2(displayPoint.x, displayPoint.y)
            pntVec.applyMatrix3(rotateMatrix)
            return pntVec
          })
          outputCutout.displayPointList = transformedDisplayPointList

          let isClockwise = MathsUtils.is2DPathClockwise(transformedDisplayPointList)
          if(flipPathWinding)
            isClockwise = !isClockwise
          if(!isClockwise)
            MathsUtils.reverseGlassSegmentList(outputCutout.path)
        }

        outputCutout.bounds = MathsUtils.calculateGlassSegmentListBounds(outputCutout.path)
        outputCutout.sourceCutoutBounds = MathsUtils.calculate2DPointListBounds(displayPoints)

        return outputCutout
      })
    }

    outputData.angle = rotationDegrees

    return outputData
  }


  /**
   * Transform a path by a matrix & coord system
   */
  transformPath(path, rotateMatrix, coordSystemScaleX, coordSystemScaleY) {
    return path.map(segment => {
      var outputSegment = new GlassSegment(segment.type, {
        startCornerRadius: segment.startCornerRadius,
        endCornerRadius: segment.endCornerRadius,
      })

      outputSegment.startPosition.set(segment.startPosition.x, segment.startPosition.y)
      outputSegment.endPosition.set(segment.endPosition.x, segment.endPosition.y)

      outputSegment.startPosition.applyMatrix3(rotateMatrix)
      outputSegment.endPosition.applyMatrix3(rotateMatrix)

      if(segment.type == SegmentTypes.arc) {
        outputSegment.arcCenterPosition = new THREE.Vector2(segment.arcCenterPosition.x, segment.arcCenterPosition.y)
        outputSegment.arcRadius = segment.arcRadius
        outputSegment.arcClockwise = segment.arcClockwise
        outputSegment.arcStartAngle = segment.arcStartAngle
        outputSegment.arcEndAngle = segment.arcEndAngle

        outputSegment.arcCenterPosition.applyMatrix3(rotateMatrix)

        outputSegment.arcStartAngle = MathsUtils.calculateArcPointAngle(outputSegment.arcCenterPosition, outputSegment.startPosition)
        outputSegment.arcEndAngle = MathsUtils.calculateArcPointAngle(outputSegment.arcCenterPosition, outputSegment.endPosition)

        if(coordSystemScaleX < 0)
          outputSegment.arcClockwise = !outputSegment.arcClockwise
        if(coordSystemScaleY < 0)
          outputSegment.arcClockwise = !outputSegment.arcClockwise

        outputSegment.arcUpdated()
      }

      return outputSegment
    })
  }


  /**
   * Get the closest object in the production panel for waterjet mode
   * Includes cutouts or the shape itself
   * 
   * Returns an object describing what object on the panel is closest
   * Returns null if nothing is close to the position
   */
  getClosestWaterjetObjectToPosition(posX, posY, radius, waterjetBedConfig) {
    let point = new THREE.Vector2(posX, posY)
    let transformedData = this.generateWaterjetPanelTransformedData(waterjetBedConfig)
    if(!transformedData)
      return null

    var closestObj = null

    // Check if inside the main shape
    let shapePointList = transformedData.shapePath.map(segment => segment.startPosition)
    if(MathsUtils.pointIn2DPolygon(point, shapePointList)) {
      closestObj = {
        type: 'panel',
        distance: radius * 2    // Large distance so any cutouts nearby override the panel click
      }
    }

    // Check if a cutout is near the position
    this.calculatedCutoutPaths.forEach((cutout, index) => {
      cutout.path.forEach(segment => {
        let projected = segment.projectPointOnSegment(point)
        let dist = projected.distanceTo(point)
        if(dist <= radius) {
          if((closestObj && dist < closestObj.distance) || !closestObj) {
            closestObj = {
              type: 'cutout',
              distance: dist,
              cutout: cutout,
              cutoutIndex: index
            }
          }
        }
      })

      // Check relief cuts?
      if(cutout.additionalCutPaths) {
        cutout.additionalCutPaths.forEach(additionalPath => {
          additionalPath.forEach(segment => {
            let projected = segment.projectPointOnSegment(point)
            let dist = projected.distanceTo(point)
            if(dist <= radius) {
              if((closestObj && dist < closestObj.distance) || !closestObj) {
                closestObj = {
                  type: 'cutout',
                  distance: dist,
                  cutout: cutout,
                  cutoutIndex: index
                }
              }
            }
          })
        })
      }
    })

    return closestObj
  }


  /**
   * Get the waterjet relief cut list for a cutout index
   */
  getWaterjetReliefCuts(cutoutIndex) {
    if(!this.panelHasShapeData)
      return []

    return this.waterjetReliefCuts.filter(relief => relief.cutoutIndex == cutoutIndex)
  }


  /**
   * Create an inline waterjet relief cut to a corner/edge cutout
   * The CNC paths will need to be recalculated for changes to apply
   */
  addWaterjetInlineReliefCut(cutoutIndex, distanceFromEdge, reliefCutHeight) {
    if(!this.panelHasShapeData)
      return

    // Only 1 inline relief cut can exist per cutout
    let existingReliefCut = this.waterjetReliefCuts.find(relief => relief.cutoutIndex == cutoutIndex && relief.type == ProductionWaterjetReliefTypes.inline)
    if(existingReliefCut)
      throw 'An inline relief cut already exists in this cutout'

    var cutout = this.calculatedCutoutPaths[cutoutIndex]
    if(!cutout)
      throw 'Cutout not found'

    var inlineReliefCut = {
      cutoutIndex: cutoutIndex,
      type: ProductionWaterjetReliefTypes.inline,
      distanceFromEdge: distanceFromEdge,
      reliefCutHeight: reliefCutHeight,
      valid: true,       // Note: The transform/recalc functions will generate the actual path & calculate if valid or not
      errorMessage: ''
    }
    this.waterjetReliefCuts.push(inlineReliefCut)
  }


  /**
   * Create an large waterjet relief cut to a corner/edge cutout
   * The CNC paths will need to be recalculated for changes to apply
   */
  addWaterjetLargeReliefCut(cutoutIndex, distanceInwards) {
    if(!this.panelHasShapeData)
      return

    // Only 1 large relief cut can exist per cutout
    let existingReliefCut = this.waterjetReliefCuts.find(relief => relief.cutoutIndex == cutoutIndex && relief.type == ProductionWaterjetReliefTypes.large)
    if(existingReliefCut)
      throw 'A large relief cut already exists in this cutout'

    var cutout = this.calculatedCutoutPaths[cutoutIndex]
    if(!cutout)
      throw 'Cutout not found'

    var largeReliefCut = {
      cutoutIndex: cutoutIndex,
      type: ProductionWaterjetReliefTypes.large,
      distanceInwards: distanceInwards,
      valid: true,       // Note: The transform/recalc functions will generate the actual path & calculate if valid or not
      errorMessage: ''
    }
    this.waterjetReliefCuts.push(largeReliefCut)    
  }


  /**
   * Remove all relief cuts from the panel at the cutout
   * The CNC paths will need to be recalculated for changes to apply
   */
  removeWaterjetReliefCuts(cutoutIndex) {
    this.waterjetReliefCuts = this.waterjetReliefCuts.filter(relief => relief.cutoutIndex != cutoutIndex)
  }


  /**
   * Move the cutting order of a cutout up or down the list
   */
  changeCutoutOrder(cutoutIndex, direction) {
    var cutout = this.calculatedCutoutPaths[cutoutIndex]
    if(!cutout)
      throw 'Cutout not found'

    if(direction < 0) {
      var prevCutout = this.calculatedCutoutPaths.find(listCutout => listCutout.cutOrder == cutout.cutOrder-1)
      if(prevCutout) {
        let tmp = cutout.cutOrder
        cutout.cutOrder = prevCutout.cutOrder
        prevCutout.cutOrder = tmp
      }
    }
    else if(direction > 0) {
      var nextCutout = this.calculatedCutoutPaths.find(listCutout => listCutout.cutOrder == cutout.cutOrder+1)
      if(nextCutout) {
        let tmp = cutout.cutOrder
        cutout.cutOrder = nextCutout.cutOrder
        nextCutout.cutOrder = tmp
      }
    }
  }


  /**
   * Set the enabled flag for a cutout
   */
  setWaterjetCutoutEnabled(cutoutIndex, enabled) {
    var cutoutSettings = this.waterjetCutoutSettings[cutoutIndex] || {}
    cutoutSettings.enabled = enabled
    this.waterjetCutoutSettings[cutoutIndex] = cutoutSettings    
  }


  /**
   * Generate the CNC cutting path DXF file
   * Returns the raw text data for the DXF file
   */
  generateCNCPathDXF(productionStation, waterjetBedConfig) {
    switch(productionStation.hardwareType) {
      case ProductionStationHardwareTypes.waterjetJetstream:
        return this._generateCNCPathJetstreamDXF(waterjetBedConfig)

      case ProductionStationHardwareTypes.waterjetGeneric:
        return this._generateCNCPathGenericDXF(waterjetBedConfig)

      default:
        throw new Error(`Waterjet hardware type ${productionStation.hardwareType} not supported for exporting`)
    }
  }


  /**
   * Generate the CNC cutting path DXF file for Jetstream specific waterjet hardware
   * Returns the raw text data for the DXF file
   */
  _generateCNCPathJetstreamDXF(waterjetBedConfig) {
    let transformedData = this.generateWaterjetPanelTransformedData(waterjetBedConfig)

    // Sort by cutting order
    var exportCutoutPaths = this.calculatedCutoutPaths.filter(cutout => cutout.enabled)
    exportCutoutPaths.sort((cutoutA, cutoutB) => {
      return cutoutA.cutOrder - cutoutB.cutOrder
    })

    let dxfWriter = new DXFWriter()
    dxfWriter.startSection('ENTITIES')

    dxfWriter.setCurrentLayer('Outline')

    if(transformedData.edgePoints) {
      transformedData.edgePoints.forEach((edgePoint, index) => {
        let nextEdgePoint = transformedData.edgePoints[(index+1) % transformedData.edgePoints.length]

        dxfWriter.writeLineEntity(edgePoint.x, edgePoint.y, nextEdgePoint.x, nextEdgePoint.y)
      })      
    }

    dxfWriter.setCurrentLayer('Cutting')

    exportCutoutPaths.forEach(cutout => {

      // If a circle center cutout, do something special
      if(cutout.type == OrderEditorCutoutTypes.centerCutOut) {
        if(cutout.geometryShapeType == OrderEditorGeometryShapeTypes.circle) {
          let boundsCenter = cutout.bounds.getCenter(new THREE.Vector2())

          // Add in a circle & place the start point directly in the center
          dxfWriter.writeCircleEntity(boundsCenter.x, boundsCenter.y, cutout.radius)

          // Cutting entry point in the middle
          dxfWriter.writePointEntity(boundsCenter.x, boundsCenter.y)
          return
        }
      }

      if(cutout.path.length == 0)
        return

      // Additional cut paths (for large relief cuts)
      // They must be done first
      if(cutout.additionalCutPaths) {
        cutout.additionalCutPaths.forEach(additionalCut => {
          if(additionalCut.length == 0)
            return

          // Start cutting entry point
          dxfWriter.writePointEntity(additionalCut[0].startPosition.x, additionalCut[0].startPosition.y)
          
          this.writeDXFPath(dxfWriter, additionalCut)          
        })
      }
      
      // Cutting paths
      // Start cutting entry point
      dxfWriter.writePointEntity(cutout.entryPoint.x, cutout.entryPoint.y)

      // Convert the cutout path (including lead in / out) into a single POLYLINE entity to avoid any connections to the shape
      // Polyline fails most of the time with Jetstream
      // Disabled until we solve other issues or Jake gets back to me with solution
      //this.writeDXFPolylinePath(dxfWriter, [cutout.leadInPath, cutout.path, cutout.leadOutPath], { windingReversed: cutout.windingReversed })

      // Note: Lead In/Out paths already flipped based on winding when generated
      this.writeDXFPath(dxfWriter, cutout.leadInPath, { windingReversed: cutout.windingReversed })
      this.writeDXFPath(dxfWriter, cutout.path, { windingReversed: cutout.windingReversed })
      this.writeDXFPath(dxfWriter, cutout.leadOutPath, { windingReversed: cutout.windingReversed })
    })

    return dxfWriter.export()
  }


  /**
   * Generate the CNC cutting path DXF file for Generic specific waterjet hardware
   * Returns the raw text data for the DXF file
   */
  _generateCNCPathGenericDXF(waterjetBedConfig) {
    let transformedData = this.generateWaterjetPanelTransformedData(waterjetBedConfig)

    let dxfWriter = new DXFWriter()
    dxfWriter.startSection('ENTITIES')

    dxfWriter.setCurrentLayer('Outline')

    if(transformedData.shapePath) {
      this.writeDXFPath(dxfWriter, transformedData.shapePath, { pathLooped: true })
    }

    // Center cutouts
    // Edge/Corner cutouts handled via the path above
    this.calculatedCutoutPaths.filter(cutout => cutout.enabled).forEach(cutout => {
      if(cutout.path.length == 0)
        return

      if(cutout.type == OrderEditorCutoutTypes.centerCutOut) {

        this.writeDXFPath(dxfWriter, cutout.path, { windingReversed: cutout.windingReversed, pathLooped: true })
      }
    })


    return dxfWriter.export()
  }


  /**
   * Write a series of paths as a single polyline entity
   * Used to avoid any connections with other entities that can be detected by the CNC/Jetstream software
   */
  writeDXFPolylinePath(dxfWriter, pathList, windingReversed) {
    let polylineVertexList = []

    pathList.forEach(path => {
      let lastPos = new THREE.Vector2(0, 0)
      path.forEach((segment, segmentIndex) => {
        let prevSegment = path[segmentIndex == 0 ? path.length-1 : segmentIndex-1]
        let nextSegment = path[segmentIndex == path.length-1 ? 0 : segmentIndex+1]
  
        if(segment.type == SegmentTypes.line) {
          var startPos = new THREE.Vector2(segment.startPosition.x, segment.startPosition.y)
          var endPos = new THREE.Vector2(segment.endPosition.x, segment.endPosition.y)
  
          // Start corner radius
          if(segmentIndex > 0 && segment.startCornerRadius > 0) {
            let roundCornerData = MathsUtils.calculateCornerRounding(
              new THREE.Vector2(prevSegment.startPosition.x, prevSegment.startPosition.y),
              new THREE.Vector2(prevSegment.endPosition.x, prevSegment.endPosition.y),
              new THREE.Vector2(segment.startPosition.x, segment.startPosition.y),
              new THREE.Vector2(segment.endPosition.x, segment.endPosition.y),
              segment.startCornerRadius,
              windingReversed
            )
  
            if(roundCornerData) {
              polylineVertexList.push({
                x: roundCornerData.segment1End.x,
                y: roundCornerData.segment1End.y,
                bulge: Math.tan(roundCornerData.angleDistRads * 0.25) * (roundCornerData.clockwise ? -1 : 1)
              })
  
              startPos = roundCornerData.segment2Start.clone()
            }
          }
  
          // If end corner radius is used, just pull back the end pos
          // The next segment will handle the arc for the corner
          if(segment.endCornerRadius > 0 && segmentIndex < path.length-1) {
            let roundCornerData = MathsUtils.calculateCornerRounding(
              new THREE.Vector2(segment.startPosition.x, segment.startPosition.y),
              new THREE.Vector2(segment.endPosition.x, segment.endPosition.y),
              new THREE.Vector2(nextSegment.startPosition.x, nextSegment.startPosition.y),
              new THREE.Vector2(nextSegment.endPosition.x, nextSegment.endPosition.y),
              segment.endCornerRadius,
              windingReversed
            )
            if(roundCornerData) {
              endPos = roundCornerData.segment1End.clone()
            }
          }

          polylineVertexList.push({
            x: startPos.x,
            y: startPos.y,
            bulge: 0
          })
          lastPos.copy(endPos)
        }
  
        else if(segment.type == SegmentTypes.arc) {
          let startAngle = MathsUtils.wrapAngleDegrees(segment.arcStartAngle)
          let endAngle = MathsUtils.wrapAngleDegrees(segment.arcEndAngle)
  
          var outputStartAngle = !segment.arcClockwise ? endAngle : startAngle
          var outputEndAngle = !segment.arcClockwise ? startAngle : endAngle
  
          let angleDistance = MathsUtils.calculateAngleDistanceDegrees(outputStartAngle, outputEndAngle, segment.arcClockwise)
  
          polylineVertexList.push({
            x: segment.startPosition.x,
            y: segment.startPosition.y,
            bulge: Math.tan(MathsUtils.degreesToRadians(Math.abs(angleDistance)) * 0.25) * (segment.arcClockwise ? 1 : -1)
          })
          lastPos.copy(segment.endPosition)
        }
      })

      if(path.length > 0) {
        polylineVertexList.push({
          x: lastPos.x,
          y: lastPos.y,
          bulge: 0
        })
      }
    })

    // Remove duplicates
    let outputVertexList = []
    polylineVertexList.forEach(vertex => {
      let curPoint = outputVertexList.find(curVertex => {
        return MathsUtils.check2DPointsEqual(vertex, curVertex)
      })
      if(!curPoint)
        outputVertexList.push(vertex)
      else
        curPoint.bulge = vertex.bulge
    })


    if(outputVertexList.length > 0) {
      dxfWriter.writeLWPolylineEntity(outputVertexList)
    }
  }


  /**
   * Write a path from the shape data to the DXF writer
   */
  writeDXFPath(dxfWriter, path, options = {}) {

    var windingReversed = !!options.windingReversed
    var pathLooped = !!options.pathLooped

    path.forEach((segment, segmentIndex) => {
      let prevSegment = path[segmentIndex == 0 ? path.length-1 : segmentIndex-1]
      let nextSegment = path[segmentIndex == path.length-1 ? 0 : segmentIndex+1]

      if(segment.type == SegmentTypes.line) {
        var startPos = new THREE.Vector2(segment.startPosition.x, segment.startPosition.y)
        var endPos = new THREE.Vector2(segment.endPosition.x, segment.endPosition.y)

        // Start corner radius
        if((pathLooped || segmentIndex > 0) && segment.startCornerRadius > 0) {
          let roundCornerData = MathsUtils.calculateCornerRounding(
            new THREE.Vector2(prevSegment.startPosition.x, prevSegment.startPosition.y),
            new THREE.Vector2(prevSegment.endPosition.x, prevSegment.endPosition.y),
            new THREE.Vector2(segment.startPosition.x, segment.startPosition.y),
            new THREE.Vector2(segment.endPosition.x, segment.endPosition.y),
            segment.startCornerRadius,
            windingReversed
          )

          if(roundCornerData) {
            let startAngle = MathsUtils.wrapAngleDegrees(MathsUtils.radiansToDegrees(roundCornerData.startAngle))
            let endAngle = MathsUtils.wrapAngleDegrees(MathsUtils.radiansToDegrees(roundCornerData.endAngle))

            let outputStartAngle = roundCornerData.clockwise ? endAngle : startAngle
            let outputEndAngle = roundCornerData.clockwise ? startAngle : endAngle

            // Add arc
            dxfWriter.writeArcEntity(roundCornerData.center.x, roundCornerData.center.y, roundCornerData.radius, outputStartAngle, outputEndAngle)

            startPos = roundCornerData.segment2Start.clone()
          }
        }

        // If end corner radius is used, just pull back the end pos
        // The next segment will handle the arc for the corner
        if(segment.endCornerRadius > 0 && (pathLooped || segmentIndex < path.length-1)) {
          let roundCornerData = MathsUtils.calculateCornerRounding(
            new THREE.Vector2(segment.startPosition.x, segment.startPosition.y),
            new THREE.Vector2(segment.endPosition.x, segment.endPosition.y),
            new THREE.Vector2(nextSegment.startPosition.x, nextSegment.startPosition.y),
            new THREE.Vector2(nextSegment.endPosition.x, nextSegment.endPosition.y),
            segment.endCornerRadius,
            windingReversed
          )
          if(roundCornerData) {
            endPos = roundCornerData.segment1End.clone()
          }
        }

        dxfWriter.writeLineEntity(startPos.x, startPos.y, endPos.x, endPos.y)
      }

      else if(segment.type == SegmentTypes.arc) {
        let startAngle = MathsUtils.wrapAngleDegrees(segment.arcStartAngle)
        let endAngle = MathsUtils.wrapAngleDegrees(segment.arcEndAngle)
        let arcClockwise = !segment.arcClockwise

        var outputStartAngle = arcClockwise ? endAngle : startAngle
        var outputEndAngle = arcClockwise ? startAngle : endAngle

        dxfWriter.writeArcEntity(segment.arcCenterPosition.x, segment.arcCenterPosition.y, segment.arcRadius, outputStartAngle, outputEndAngle)
      }
    })
  }


  /**
   * Write the waterjet Jetstream start cutting marker position
   * Tells the hardware to start cutting
   * Deprecated
   */
  writeDXFWaterjetEntryPosition(writer, position) {
    // The TCPointEntity header
    // Any point probably works, but we'll use the TCPointEntity class as other examples use that
    // Jetstream recognises a point and connected path as the start of the cutting

    writer.writeCode(0, 'TCPOINTENTITY')
    writer.writeCode(8, '0')            // Layer
    writer.writeCode(6, 'Continuous')   // Linetype
    writer.writeCode(160, '               176')          // Unknown
    writer.writeCode(100, 'AcDbPoint')
    writer.writePointValueCode(10, position.x)
    writer.writePointValueCode(20, position.y)
    writer.writePointValueCode(30, 0)
    writer.writeCode(100, 'TcPointEntity')
    writer.writeCode(70, '     1')           // Unknown
    writer.writeCode(71, '     3')           // Unknown
    writer.writeCode(40, '2.5')         // Unknown
  }

}